import React, { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "zm_a2hs_dismissed_until";
const DISMISS_DAYS = 7;

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const isIOS = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) &&
  !window.MSStream;

const dismissedRecently = () => {
  const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return until && Date.now() < until;
};

// Bottom A2HS banner. Shows the native install prompt on Android/Chrome, and a
// "share → add to home screen" hint on iOS Safari. Hidden when already installed
// or dismissed within the last 7 days.
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || dismissedRecently()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS has no beforeinstallprompt → show manual guidance.
    let iosTimer;
    if (isIOS()) {
      iosTimer = setTimeout(() => {
        setIosHint(true);
        setVisible(true);
      }, 1500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(
      DISMISS_KEY,
      String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000)
    );
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch (e) {
      /* user dismissed */
    }
    setDeferred(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 bottom-20 z-[70] w-[calc(100%-2rem)] max-w-md"
      data-testid="a2hs-banner"
    >
      <div className="flex items-center gap-3 rounded-2xl bg-white border border-blue-200 shadow-xl px-4 py-3">
        <div className="w-10 h-10 rounded-xl bg-[#2563EB] flex items-center justify-center shrink-0">
          <Download className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          {iosHint ? (
            <>
              <p className="text-sm font-black text-gray-900">제로마진 앱 설치</p>
              <p className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-1">
                공유 버튼 <Share className="w-3 h-3 inline" /> → '홈 화면에 추가'를 눌러주세요
              </p>
            </>
          ) : (
            <p className="text-sm font-bold text-gray-900 leading-snug">
              홈 화면에 제로마진을 추가하고 앱처럼 사용하세요
            </p>
          )}
        </div>
        {!iosHint && (
          <button
            data-testid="a2hs-install-btn"
            onClick={install}
            className="shrink-0 rounded-xl bg-[#2563EB] text-white text-xs font-black px-3.5 py-2 tap-scale"
          >
            추가하기
          </button>
        )}
        <button
          data-testid="a2hs-dismiss-btn"
          onClick={dismiss}
          aria-label="닫기"
          className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
